import {
  DEALER_SEARCH_KEY,
  EARTH_RADIUS,
  NEARBY_RADIUS,
  NEARBY_RADIUS_RANGE,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { TOP_NAV_HEIGHT } from '@/app/constants/top-navigation';
import type { DealerGrouping } from '@/services/contentstack/models/dealer';

export interface DealerLocalParams {
  [DEALER_SEARCH_KEY.Q]?: string;
  [DEALER_SEARCH_KEY.Lat]?: string;
  [DEALER_SEARCH_KEY.Lng]?: string;
  [DEALER_SEARCH_KEY.ServiceType]?: string[];
  [DEALER_SEARCH_KEY.VehicleType]?: string[];
  [DEALER_SEARCH_KEY.BusinessDay]?: string[];
}

interface Flags {
  businessDay: string[];
  hasLcv: boolean;
  hasCv: boolean;
  hasParamsShowroom: boolean;
  hasParamsService: boolean;
  hasParamsBodyPaint: boolean;
  hasSpecialChangeTire: boolean;
  hasSpecialOverhaul: boolean;
  hasServiceParam: boolean;
}

const getFlags = (query?: URLSearchParams | null) => {
  let vehicleType = query?.getAll(DEALER_SEARCH_KEY.VehicleType);

  if (!vehicleType || vehicleType?.length === 0) {
    vehicleType = [`${process.env.NEXT_PUBLIC_WEB}`];
  }

  const serviceType = query?.getAll(DEALER_SEARCH_KEY.ServiceType) || [];
  const specialService = query?.getAll(DEALER_SEARCH_KEY.Service) || [];
  const businessDay = query?.getAll(DEALER_SEARCH_KEY.BusinessDay) || [];

  const hasLcv = vehicleType.includes('lcv');
  const hasCv = vehicleType.includes('cv');

  const hasParamsShowroom = serviceType.includes(DEALER_SEARCH_KEY.Showroom);
  const hasParamsService =
    serviceType.includes(DEALER_SEARCH_KEY.Service) ||
    specialService?.includes(DEALER_SEARCH_KEY.ChangeTire) ||
    specialService?.includes(DEALER_SEARCH_KEY.Overhaul);

  const hasParamsBodyPaint = serviceType.includes(DEALER_SEARCH_KEY.BodyPaint);
  const hasSpecialChangeTire = specialService?.includes(
    DEALER_SEARCH_KEY.ChangeTire
  );
  const hasSpecialOverhaul = specialService?.includes(
    DEALER_SEARCH_KEY.Overhaul
  );

  const hasServiceParam = serviceType.includes(DEALER_SEARCH_KEY.Service);

  return {
    businessDay: businessDay || [],
    hasLcv: hasLcv || false,
    hasCv: hasCv || false,
    hasParamsShowroom: hasParamsShowroom || false,
    hasParamsService: hasParamsService || false,
    hasParamsBodyPaint: hasParamsBodyPaint || false,
    hasSpecialChangeTire: hasSpecialChangeTire || false,
    hasSpecialOverhaul: hasSpecialOverhaul || false,
    hasServiceParam: hasServiceParam || false,
  };
};

const filterDealer = (dealer: DealerSearch, flags: Flags) => {
  const {
    businessDay,
    hasLcv,
    hasCv,
    hasParamsShowroom,
    hasParamsService,
    hasParamsBodyPaint,
    hasSpecialChangeTire,
    hasSpecialOverhaul,
    hasServiceParam,
  } = flags;

  let shouldVisible = false;

  const dealerCapabilities = {
    sale: {
      lcv: dealer.sale?.active_lcv || false,
      cv: dealer.sale?.active_cv || false,
    },
    aftersale: {
      lcv: dealer.aftersale?.active_lcv || false,
      cv: dealer.aftersale?.active_cv || false,
    },
    bp: {
      lcv: dealer.bp?.active_lcv || false,
      cv: dealer.bp?.active_cv || false,
    },
    ['aftersale.tire_changing']: {
      lcv: dealer.aftersale?.tire_changing_lcv || false,
      cv: dealer.aftersale?.tire_changing_cv || false,
    },
    ['aftersale.overhaul']: {
      lcv: dealer.aftersale?.overhaul_lcv || false,
      cv: dealer.aftersale?.overhaul_cv || false,
    },
  };

  const checkServiceCapability = (
    key: keyof typeof dealerCapabilities,
    hasLcv: boolean,
    hasCv: boolean
  ): boolean => {
    const capability = dealerCapabilities[key];

    if (hasLcv && hasCv) {
      return capability.lcv && capability.cv;
    } else if (hasLcv && !hasCv) {
      return capability.lcv;
    } else if (hasCv && !hasLcv) {
      return capability.cv;
    }

    return false;
  };

  const checkBusinessDay = (
    key: keyof typeof dealerCapabilities,
    query?: string[]
  ) => {
    let showBusinessDay = true;

    if (!query || query?.length === 0) return showBusinessDay;

    if (query.includes(DEALER_SEARCH_KEY.Weekdays)) {
      showBusinessDay =
        showBusinessDay &&
        !!dealer[key]?.open_time?.hour_mon_fri &&
        dealer[key]?.open_time?.hour_mon_fri !== 'ปิดทำการ';
    }

    if (query.includes(DEALER_SEARCH_KEY.Saturday)) {
      showBusinessDay =
        showBusinessDay &&
        !!dealer[key]?.open_time?.hour_sat &&
        dealer[key]?.open_time?.hour_sat !== 'ปิดทำการ';
    }

    if (query.includes(DEALER_SEARCH_KEY.Sunday)) {
      showBusinessDay =
        showBusinessDay &&
        !!dealer[key]?.open_time?.hour_sun &&
        dealer[key]?.open_time?.hour_sun !== 'ปิดทำการ';
    }

    return showBusinessDay;
  };

  const hasBodyPaint = dealerCapabilities.bp.lcv || dealerCapabilities.bp.cv;

  const hasCapabilityShowroom = checkServiceCapability('sale', hasLcv, hasCv);

  const hasCapabilityService = checkServiceCapability(
    'aftersale',
    hasLcv,
    hasCv
  );
  const hasCapabilityBodyPaint = checkServiceCapability('bp', hasLcv, hasCv);
  const hasCapabilityChangeTire = checkServiceCapability(
    'aftersale.tire_changing',
    hasLcv,
    hasCv
  );
  const hasCapabilityOverhaul = checkServiceCapability(
    'aftersale.overhaul',
    hasLcv,
    hasCv
  );

  const checkSpecialService = () => {
    // Have all services
    if (hasServiceParam && hasSpecialChangeTire && hasSpecialOverhaul) {
      return (
        hasCapabilityService && hasCapabilityChangeTire && hasCapabilityOverhaul
      );
    }

    // Have only Change Tire
    if (hasSpecialChangeTire) {
      return hasCapabilityService && hasCapabilityChangeTire;
    }

    // Have only Overhaul
    if (hasSpecialOverhaul) {
      return hasCapabilityService && hasCapabilityOverhaul;
    }

    // Have Service only
    if (hasServiceParam) {
      return hasCapabilityService;
    }

    return false;
  };

  // Condition 1: Select Showroom only
  if (hasParamsShowroom && !hasParamsService && !hasParamsBodyPaint) {
    const hasValidBusinessDay = checkBusinessDay('sale', businessDay);
    shouldVisible = hasCapabilityShowroom && hasValidBusinessDay;
  }

  // Condition 2: Select Service only
  else if (hasParamsService && !hasParamsShowroom && !hasParamsBodyPaint) {
    const hasValidBusinessDay = checkBusinessDay('aftersale', businessDay);

    shouldVisible = checkSpecialService() && hasValidBusinessDay;
  }

  // Condition 3: Select Body Paint only
  else if (hasParamsBodyPaint && !hasParamsShowroom && !hasParamsService) {
    const hasValidBusinessDay = checkBusinessDay('bp', businessDay);
    shouldVisible = hasCapabilityBodyPaint && hasValidBusinessDay;
  }

  // Condition 4: Select multiple conditions
  else if (hasParamsShowroom || hasParamsService || hasParamsBodyPaint) {
    const capabilityCheck =
      (hasParamsShowroom ? hasCapabilityShowroom : true) &&
      (hasParamsService ? checkSpecialService() : true) &&
      (hasParamsBodyPaint ? hasCapabilityBodyPaint : true);

    const businessDayCheck =
      (hasParamsShowroom ? checkBusinessDay('sale', businessDay) : true) &&
      (hasParamsService ? checkBusinessDay('aftersale', businessDay) : true) &&
      (hasParamsBodyPaint ? checkBusinessDay('bp', businessDay) : true);

    shouldVisible = capabilityCheck && businessDayCheck;
  }

  // Condition 5: No params selected (Display all)
  else {
    shouldVisible =
      hasCapabilityService || hasCapabilityShowroom || hasCapabilityBodyPaint;

    if (businessDay.length > 0) {
      shouldVisible =
        checkBusinessDay('sale', businessDay) ||
        checkBusinessDay('aftersale', businessDay) ||
        checkBusinessDay('bp', businessDay);
    }
  }

  return shouldVisible;
};

export const searchDealersByKeyword = (
  items: DealerSearch[],
  keyword?: string,
  query?: URLSearchParams | null,
  currentLocation?: { lat: number; lng: number }
) => {
  const searchText = keyword?.toLowerCase().trim() || '';
  const flags = getFlags(query);

  const result = items.filter((dealer) => {
    let isMatchQuery: boolean = false;
    dealer.matchs = {};

    if (currentLocation?.lat && currentLocation?.lng) {
      dealer.distance = calculateDistance(
        currentLocation.lat,
        currentLocation.lng,
        +(dealer.lat || 0),
        +(dealer.lng || 0)
      );
    }

    if (query !== null) {
      isMatchQuery = !!filterDealer(dealer, flags);
    }

    if (!isMatchQuery) return isMatchQuery;

    if (!searchText) return true;

    const saleNameMatch = (dealer.sale?.name_th || '')
      .toLowerCase()
      .includes(searchText);

    const saleAddressMatch = (dealer.sale?.address || '')
      .toLowerCase()
      .includes(searchText);

    const aftersaleNameMatch = (dealer.aftersale?.name_th || '')
      .toLowerCase()
      .includes(searchText);

    const aftersaleAddressMatch = dealer.aftersale?.address
      ?.toLowerCase()
      .includes(searchText);

    const bpNameMatch = (dealer.bp?.name_th || '')
      .toLowerCase()
      .includes(searchText);

    const bpAddressMatch = (dealer.bp?.address || '')
      .toLowerCase()
      .includes(searchText);

    if (saleNameMatch) {
      dealer.matchs.name = dealer.sale?.name_th || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.Showroom;
    } else if (saleAddressMatch) {
      dealer.matchs.address = dealer.sale?.address || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.Showroom;
    } else if (aftersaleNameMatch) {
      dealer.matchs.name = dealer.aftersale?.name_th || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.Service;
    } else if (aftersaleAddressMatch) {
      dealer.matchs.address = dealer.aftersale?.address || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.Service;
    } else if (bpNameMatch) {
      dealer.matchs.name = dealer.bp?.name_th || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.BodyPaint;
    } else if (bpAddressMatch) {
      dealer.matchs.address = dealer.bp?.address || '';
      dealer.matchs.type = DEALER_SEARCH_KEY.BodyPaint;
    }

    const isMatch =
      saleNameMatch ||
      saleAddressMatch ||
      aftersaleNameMatch ||
      aftersaleAddressMatch ||
      bpNameMatch ||
      bpAddressMatch;

    return isMatch;
  });

  return result.sort((a, b) => {
    if (a.distance && b.distance) {
      return a.distance - b.distance;
    }

    const nameA =
      a.matchs?.name ||
      a.sale?.name_th ||
      a.aftersale?.name_th ||
      a.bp?.name_th ||
      '';
    const nameB =
      b.matchs?.name ||
      b.sale?.name_th ||
      b.aftersale?.name_th ||
      b.bp?.name_th ||
      '';

    return nameA.localeCompare(nameB, 'th');
  });
};

export const searchByKeys = ({
  items = [],
  seachKeys = [],
  keyword,
  field,
}: {
  items: any[];
  seachKeys: string[];
  keyword: string;
  field?: string;
}) => {
  if (items.length === 0 || !keyword) {
    return items;
  }

  const searchText = keyword.toLowerCase().trim();

  const result = items.filter((item) => {
    return seachKeys.some((key) => {
      const focusKey = field ? item?.[field]?.[key] : item[key];
      if (!focusKey) {
        return false;
      }

      const value = `${focusKey}`.toLowerCase();
      return value.includes(searchText);
    });
  });

  return result;
};

export const searchSuggestPlaces = (items: any[] = [], keyword: string) => {
  const result = searchByKeys({
    items,
    keyword,
    seachKeys: ['name'],
  });

  if (result.length === 0) return [];

  const places = result.sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'th')
  );

  const filterPlaces: any[] = [];

  for (const place of places) {
    if (filterPlaces.length >= 3) break;
    const isExist = filterPlaces.some((item) => {
      if (item?.province) {
        return item.name === place.name && item.province === place.province;
      }
      return item.name === place.name;
    });
    if (!isExist) {
      filterPlaces.push(place);
    }
  }

  return filterPlaces;
};

export const searchDealerWithQuery = (
  items: DealerGrouping[] = [],
  keyword: string,
  query?: DealerLocalParams | null
): DealerGrouping[] => {
  let result = searchByKeys({
    items,
    keyword,
    seachKeys: ['name', 'address'],
    field: 'groupForSearch',
  }) as DealerGrouping[];

  if (result.length === 0) return [];

  if (query && Object.keys(query).length > 0) {
    result = result.filter((item) => {
      const serviceType = query[DEALER_SEARCH_KEY.ServiceType];

      if (serviceType && serviceType.length > 0) {
        return serviceType.includes(item.groupForSearch?.type || '');
      }

      return true;
    });
  }

  return result.sort((a: DealerGrouping, b: DealerGrouping) => {
    const nameA = a.groupForSearch?.name?.toLowerCase() || '';
    const nameB = b.groupForSearch?.name?.toLowerCase() || '';

    return nameA.localeCompare(nameB, 'th');
  });
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  ln2: number
) => {
  // Convert latitude and longitude from degrees to radians
  const latDifferenceRadians = (lat2 - lat1) * (Math.PI / 180);
  const lonDifferenceRadians = (ln2 - lng1) * (Math.PI / 180);

  // Haversine formula
  const squaredHalfChordLength =
    Math.sin(latDifferenceRadians / 2) * Math.sin(latDifferenceRadians / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(lonDifferenceRadians / 2) *
      Math.sin(lonDifferenceRadians / 2);

  const angularDistanceRadians =
    2 *
    Math.atan2(
      Math.sqrt(squaredHalfChordLength),
      Math.sqrt(1 - squaredHalfChordLength)
    );

  return EARTH_RADIUS * angularDistanceRadians;
};

export const findLocationsWithinRadius = (
  items: DealerSearch[],
  targetLat: number,
  targetLng: number
) => {
  if (items.length === 0) return [];

  const locationsWithinRadius = items.filter((item) => {
    const lat = +(item.lat || 0);
    const lng = +(item.lng || 0);

    const distance = calculateDistance(targetLat, targetLng, lat, lng);
    item.distanceByLocation = distance;
    return distance <= NEARBY_RADIUS;
  });

  return locationsWithinRadius.sort(
    (a, b) => (a.distanceByLocation || 0) - (b.distanceByLocation || 0)
  );
};

export const findLocationsWithRange = (
  dealers: DealerSearch[],
  map: google.maps.Map,
  currentLocation?: { lat: number; lng: number },
  isDesktop?: boolean
) => {
  const scopeDealers: DealerSearch[] = [];

  for (const radius of NEARBY_RADIUS_RANGE) {
    scopeDealers.length = 0; // clear array

    for (const dealer of dealers) {
      const distance = dealer.distance ?? '';
      if (!distance || distance > radius) continue;
      if (scopeDealers.length > 10) break;
      scopeDealers.push(dealer);
    }
  }

  if (scopeDealers.length > 0) {
    const bounds = new google.maps.LatLngBounds();
    if (currentLocation) {
      bounds.extend(
        new google.maps.LatLng(currentLocation.lat, currentLocation.lng)
      );
    }
    for (const dealer of scopeDealers) {
      bounds.extend(new google.maps.LatLng(+dealer.lat, +dealer.lng));
    }

    map.fitBounds(bounds);
  }

  if (currentLocation && scopeDealers.length > 0) {
    const center = { lat: +currentLocation.lat, lng: +currentLocation.lng };
    const offset = -TOP_NAV_HEIGHT / 2;

    map.setCenter(center);
    map.panBy(-1, isDesktop ? offset : offset + 60); // markerWidth + padding
  }
  return scopeDealers;
};

// ref: https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds
const WORLD_DIM = { height: 256, width: 256 };
const ZOOM_MAX = 21;
export const getBoundsZoomLevel = (bounds: google.maps.LatLngBounds) => {
  const mapElement = document.getElementById('dealer-map');
  if (!mapElement) return 0;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapElement.clientHeight, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapElement.clientWidth, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};
